/**
 * FOLDER UTILITIES
 *
 * Helper functions for working with folder hierarchies.
 * Used for building breadcrumb paths and other folder-related operations.
 */

/**
 * BUILD FOLDER PATH
 *
 * Constructs the full folder path from a leaf folder to root.
 * Traverses the folder tree upward using parentId references.
 *
 * @param {number|string|null} folderId - The ID of the current/leaf folder
 * @param {Array} folders - Flat list of all folders with { id, name, parentId }
 * @returns {Array} Array of folder objects from root to leaf: [{ id, name }, ...]
 *
 * @example
 * // Given folders: [{ id: 1, name: 'Root', parentId: null }, { id: 2, name: 'Child', parentId: 1 }]
 * buildFolderPath(2, folders) // Returns: [{ id: 1, name: 'Root' }, { id: 2, name: 'Child' }]
 */
export const buildFolderPath = (folderId, folders) => {
  // Return empty array if no folder selected or no folders available
  if (!folderId || !folders || folders.length === 0) {
    return []
  }

  const path = []
  let currentId = folderId

  // Traverse up the tree using parentId
  while (currentId) {
    // Find folder by ID (handle both string and number comparisons)
    const folder = folders.find(f =>
      f.id === currentId ||
      f.id === Number(currentId) ||
      String(f.id) === String(currentId)
    )

    if (!folder) {
      // Folder not found, stop traversal
      break
    }

    // Add to beginning of path (we're traversing from leaf to root)
    path.unshift({ id: folder.id, name: folder.name })

    // Move to parent
    currentId = folder.parentId
  }

  return path
}
